import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, MessageSquareIcon, CalendarIcon, CreditCardIcon, BarChart3Icon, UsersIcon, SmartphoneIcon, TrendingUpIcon, ChevronDownIcon, BuildingIcon, ZapIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function CrmBusinessToolsPage() {
  const faqs = [{
    question: 'What features are included in the CRM?',
    answer: 'Our CRM includes a unified inbox for all communications (text, email, social, lead platforms), a drag-and-drop sales pipeline, automated appointment scheduling, invoicing and payment processing, and detailed reporting dashboards.'
  }, {
    question: 'Does it integrate with my lead generation efforts?',
    answer: 'Yes. Whether a lead comes from a paid ad, a lead platform message, a website form, or a direct phone call, it all funnels into one central dashboard. You never have to switch between apps to reply to customers.'
  }, {
    question: 'Can I process payments through the CRM?',
    answer: 'Yes. You can send professional, branded invoices via text or email and collect payments instantly using Stripe, Square, or PayPal integrations.'
  }, {
    question: 'Is there a mobile app?',
    answer: 'Yes. You and your team can download our mobile app (iOS and Android) to manage conversations, view your calendar, and send invoices directly from the field.'
  }, {
    question: 'How hard is it to migrate from my current system?',
    answer: 'We handle the heavy lifting. We can import your existing contacts and set up your pipelines so you can hit the ground running without losing historical data.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-1148">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1149">
          <span className="text-lg font-bold text-white group-hover:text-blue-500 transition-colors" data-id="element-1150">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} data-id="element-1151" />
        </button>
        <AnimatePresence data-id="element-1152">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-1153">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1154">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-1155">
      <SEOHead title="CRM & Business Tools for Home Services | Phoenix | Viridia Analytics" description="Centralize your home service operations with our robust CRM platform in Phoenix. Manage leads, track pipelines, and integrate with our AI lead conversion system." canonical="/crm-business-tools" keywords="CRM for local business, business management tools, lead tracking software, automated invoicing, appointment scheduling software, Phoenix business tools, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers" faq={faqs} data-id="element-1156" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-1157">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1158">
          <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500/10 blur-[120px] animate-pulse-slow" data-id="element-1159"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1160">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1161">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-1162">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-1163">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-sm font-semibold tracking-wide mb-6" data-id="element-1164">
                  SUPPORTING INFRASTRUCTURE
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1165">
                  Run Your Entire Business From{' '}
                  <span className="text-blue-500" data-id="element-1166">One Place</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1167">
                  Stop jumping between disconnected apps. Manage leads, customer
                  conversations, bookings, invoicing, and revenue visibility
                  from a single, centralized command center that integrates
                  perfectly with your lead conversion system.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1168">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-1169">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-1170">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Command Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-1171">
              <div className="absolute inset-0 bg-gradient-to-tr from-viridia-surface to-gray-900 rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6" data-id="element-1172">
                {/* Header */}
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-1173">
                  <div className="flex gap-2" data-id="element-1174">
                    <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-1175"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-1176"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-1177"></div>
                  </div>
                  <div className="text-sm font-medium text-gray-400" data-id="element-1178">
                    Business Command Center
                  </div>
                </div>

                {/* Modular Panels */}
                <div className="grid grid-cols-3 gap-4 h-[calc(100%-4rem)]" data-id="element-1179">
                  {/* Left Col - Pipeline */}
                  <div className="col-span-1 space-y-4" data-id="element-1180">
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/2" data-id="element-1181">
                      <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-bold" data-id="element-1182">
                        Active Pipeline
                      </div>
                      <div className="space-y-2" data-id="element-1183">
                        <div className="h-8 bg-blue-500/10 rounded border border-blue-500/20 flex items-center px-3" data-id="element-1184">
                          <span className="text-xs text-blue-400" data-id="element-1185">
                            New Leads (4)
                          </span>
                        </div>
                        <div className="h-8 bg-yellow-500/10 rounded border border-yellow-500/20 flex items-center px-3" data-id="element-1186">
                          <span className="text-xs text-yellow-400" data-id="element-1187">
                            Estimates Sent (2)
                          </span>
                        </div>
                        <div className="h-8 bg-viridia/10 rounded border border-viridia/20 flex items-center px-3" data-id="element-1188">
                          <span className="text-xs text-viridia" data-id="element-1189">
                            Jobs Won (5)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/2" data-id="element-1190">
                      <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-bold" data-id="element-1191">
                        Recent Comms
                      </div>
                      <div className="space-y-3" data-id="element-1192">
                        <div className="flex gap-2 items-center" data-id="element-1193">
                          <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0" data-id="element-1194"></div>
                          <div className="h-2 bg-gray-700 rounded w-full" data-id="element-1195"></div>
                        </div>
                        <div className="flex gap-2 items-center" data-id="element-1196">
                          <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0" data-id="element-1197"></div>
                          <div className="h-2 bg-gray-700 rounded w-3/4" data-id="element-1198"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Col - Calendar & Revenue */}
                  <div className="col-span-2 space-y-4" data-id="element-1199">
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-2/3 relative overflow-hidden" data-id="element-1200">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/10 rounded-full blur-2xl" data-id="element-1201"></div>
                      <div className="text-xs text-gray-500 mb-4 uppercase tracking-wider font-bold" data-id="element-1202">
                        Revenue Snapshot
                      </div>
                      <div className="text-3xl font-bold text-white mb-1" data-id="element-1203">
                        $24,850
                      </div>
                      <div className="text-xs text-viridia flex items-center mb-6" data-id="element-1204">
                        <TrendingUpIcon className="w-3 h-3 mr-1" data-id="element-1205" /> +12% this
                        month
                      </div>
                      {/* Fake Chart */}
                      <div className="flex items-end gap-2 h-24 mt-auto" data-id="element-1206">
                        {[40, 60, 45, 80, 65, 90, 75].map((h, i) => <motion.div key={i} initial={{
                        height: 0
                      }} animate={{
                        height: `${h}%`
                      }} transition={{
                        duration: 1,
                        delay: 0.5 + i * 0.1
                      }} className="flex-1 bg-gradient-to-t from-viridia/20 to-viridia/60 rounded-t-sm" data-id="element-1207"></motion.div>)}
                      </div>
                    </div>
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/3 flex items-center justify-between" data-id="element-1208">
                      <div data-id="element-1209">
                        <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-bold" data-id="element-1210">
                          Next Appointment
                        </div>
                        <div className="text-sm font-medium text-white" data-id="element-1211">
                          AC Repair Estimate
                        </div>
                        <div className="text-xs text-gray-400" data-id="element-1212">
                          Today, 2:00 PM
                        </div>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center" data-id="element-1213">
                        <CalendarIcon className="w-5 h-5 text-viridia" data-id="element-1214" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CORE FEATURES */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1215">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1216">
          <ScrollReveal data-id="element-1217">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1218">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1219">
                Everything You Need to Operate
              </h2>
              <p className="text-xl text-gray-400" data-id="element-1220">
                A CRM built specifically to support local service businesses,
                keeping your pipeline organized and your follow-up consistent.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1221">
            {[{
            icon: <MessageSquareIcon className="w-6 h-6 text-white" data-id="element-1222" />,
            title: 'Unified Inbox',
            desc: 'See texts, emails, social DMs, and lead platform messages in one single thread per customer.'
          }, {
            icon: <BarChart3Icon className="w-6 h-6 text-white" data-id="element-1223" />,
            title: 'Visual Sales Pipeline',
            desc: 'Drag and drop leads through stages (New Lead → Estimate Sent → Job Won) so you always know where money is sitting.'
          }, {
            icon: <CalendarIcon className="w-6 h-6 text-white" data-id="element-1224" />,
            title: 'Automated Scheduling',
            desc: 'Let customers book directly on your calendar based on your real-time availability. No more phone tag.'
          }, {
            icon: <CreditCardIcon className="w-6 h-6 text-white" data-id="element-1225" />,
            title: 'Invoicing & Payments',
            desc: 'Send professional invoices via text or email and get paid faster with Stripe, Square, or PayPal integration.'
          }, {
            icon: <UsersIcon className="w-6 h-6 text-white" data-id="element-1226" />,
            title: 'Contact Management',
            desc: 'Store every detail, past conversation, and job history for every customer in a secure, searchable database.'
          }, {
            icon: <SmartphoneIcon className="w-6 h-6 text-white" data-id="element-1227" />,
            title: 'Mobile App',
            desc: 'Run your business from the truck. Reply to leads, check your schedule, and send invoices from your phone.'
          }].map((feature, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-1228">
                <div className="bg-viridia-black rounded-2xl p-8 border border-gray-800 h-full hover:border-viridia/50 transition-colors" data-id="element-1229">
                  <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center mb-6" data-id="element-1230">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1231">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1232">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* PIPELINE VISUALIZATION */}
      <section className="py-24 bg-viridia-black" data-id="element-1233">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1234">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1235">
            <ScrollReveal data-id="element-1236">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1237">
                Stop Losing Track of Leads
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-1238">
                When you rely on sticky notes, whiteboards, or your memory,
                leads fall through the cracks. Our CRM gives you a clear, visual
                pipeline of exactly where every potential job stands.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1239">
                <li className="flex items-start" data-id="element-1240">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-1241" />
                  <span className="text-gray-300" data-id="element-1242">
                    Know exactly who needs an estimate sent
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1243">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-1244" />
                  <span className="text-gray-300" data-id="element-1245">
                    See which estimates are waiting for approval
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1246">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-1247" />
                  <span className="text-gray-300" data-id="element-1248">
                    Track total potential revenue in your pipeline
                  </span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-viridia font-bold hover:text-viridia-dark transition-colors text-lg" data-id="element-1249">
                Get Organized Today <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1250" />
              </Link>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-1251">
              <div className="bg-viridia-surface rounded-2xl p-6 border border-gray-800 shadow-2xl" data-id="element-1252">
                <div className="flex justify-between items-center mb-6" data-id="element-1253">
                  <h3 className="text-white font-bold" data-id="element-1254">Sales Pipeline</h3>
                  <span className="text-viridia text-sm font-medium" data-id="element-1255">
                    $12,400 Potential
                  </span>
                </div>
                <div className="space-y-4" data-id="element-1256">
                  {/* Stage 1 */}
                  <div data-id="element-1257">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1258">
                      <span className="text-gray-400 font-medium" data-id="element-1259">
                        New Leads (3)
                      </span>
                      <span className="text-gray-500" data-id="element-1260">$2,100</span>
                    </div>
                    <div className="space-y-2" data-id="element-1261">
                      <div className="bg-viridia-black p-3 rounded border border-gray-800 text-sm text-gray-300" data-id="element-1262">
                        Sarah M. - AC Repair
                      </div>
                      <div className="bg-viridia-black p-3 rounded border border-gray-800 text-sm text-gray-300" data-id="element-1263">
                        Mike T. - Maintenance
                      </div>
                    </div>
                  </div>
                  {/* Stage 2 */}
                  <div data-id="element-1264">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1265">
                      <span className="text-blue-400 font-medium" data-id="element-1266">
                        Estimate Sent (2)
                      </span>
                      <span className="text-gray-500" data-id="element-1267">$4,800</span>
                    </div>
                    <div className="space-y-2" data-id="element-1268">
                      <div className="bg-viridia-black p-3 rounded border border-blue-900/30 text-sm text-gray-300" data-id="element-1269">
                        David L. - Full Install
                      </div>
                    </div>
                  </div>
                  {/* Stage 3 */}
                  <div data-id="element-1270">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1271">
                      <span className="text-viridia font-medium" data-id="element-1272">
                        Job Won (4)
                      </span>
                      <span className="text-gray-500" data-id="element-1273">$5,500</span>
                    </div>
                    <div className="space-y-2" data-id="element-1274">
                      <div className="bg-viridia-black p-3 rounded border border-viridia/30 text-sm text-gray-300" data-id="element-1275">
                        Jessica R. - Duct Cleaning
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1276">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1277">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1278">
            <ScrollReveal data-id="element-1279">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1280">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1281">
                Our CRM is designed as the supporting infrastructure for local
                service businesses that want to centralize their operations.
              </p>
              <ul className="space-y-4" data-id="element-1282">
                <li className="flex items-start gap-3" data-id="element-1283">
                  <BuildingIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1284" />
                  <div data-id="element-1285">
                    <h4 className="font-bold text-white" data-id="element-1286">
                      Disorganized Contractors
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1287">
                      If you are using sticky notes, whiteboards, or multiple
                      apps to track jobs, you need a centralized CRM.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1288">
                  <ZapIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1289" />
                  <div data-id="element-1290">
                    <h4 className="font-bold text-white" data-id="element-1291">
                      Businesses Using Our AI
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1292">
                      The CRM is the perfect companion to our AI Lead Conversion
                      system, giving you full visibility into every lead the AI
                      captures.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-1293">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-1294">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1295">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-1296">
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1297">
                    <div className="font-bold text-white mb-1" data-id="element-1298">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1299">
                      Our flagship system for instant lead response and booking.
                    </div>
                  </Link>
                  <Link to="/custom-automations" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1300">
                    <div className="font-bold text-white mb-1" data-id="element-1301">
                      Custom Automations
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1302">
                      Bespoke workflows to connect your CRM with other tools.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1303">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1304">
          <ScrollReveal data-id="element-1305">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1306">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1307">
              Everything you need to know about our CRM and business tools.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1308">
          <ScrollReveal delay={0.2} data-id="element-1309">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1310">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1311" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1312">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1313">
          <ScrollReveal data-id="element-1314">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1315">
              Ready to get organized?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1316">
              Book a strategy call to see how our CRM can streamline your
              operations and help you close more jobs.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-1317">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1318" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}